import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import { ReferralDiscount, ReferralDiscountSummary } from '../types';
import {
  ListPagination,
  REFERRAL_DISCOUNT_KIND_LABELS,
  REFERRAL_DISCOUNT_STATUS_LABELS,
  ReferralDiscountStatusBadge,
  ReferralKindBadge,
  SummaryTile,
  formatReferralDate,
  useUrlFilters,
} from './referralUi';

const FILTER_KEYS = ['customer_search', 'kind', 'status', 'start_date', 'end_date'] as const;
const INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

const pluralise = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const earnedBreakdown = (summary: ReferralDiscountSummary) =>
  [
    `${summary.earned_by_kind.welcome} welcome`,
    pluralise(summary.earned_by_kind.referrer_reward, 'referral reward'),
    pluralise(summary.earned_by_kind.admin_grant, 'admin grant'),
  ].join(', ');

const displayNotes = (discount: ReferralDiscount) => {
  if (!discount.notes) return '';
  return discount.grant_reason ? discount.notes.split('\n').slice(1).join('\n') : discount.notes;
};

const ReferralDiscountsList: React.FC = () => {
  const { values: filters, page, setFilters, setPage } = useUrlFilters(FILTER_KEYS);
  const [searchDraft, setSearchDraft] = useState(filters.customer_search);
  const [discounts, setDiscounts] = useState<ReferralDiscount[]>([]);
  const [summary, setSummary] = useState<ReferralDiscountSummary | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { customer_search, kind, status, start_date, end_date } = filters;

  useEffect(() => {
    setSearchDraft(customer_search);
  }, [customer_search]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (customer_search) query.append('customer_search', customer_search);
        if (kind) query.append('kind', kind);
        if (status) query.append('status', status);
        if (start_date) query.append('start_date', start_date);
        if (end_date) query.append('end_date', end_date);
        query.append('page', String(page));
        query.append('per_page', '10');

        const res = await fetchWithAuth(`/referral_discounts?${query.toString()}`);
        if (!res.ok) throw new Error('Failed to fetch referral discounts');

        const data = await res.json();
        setDiscounts(data.referral_discounts);
        setSummary(data.summary);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } catch (error) {
        console.error('Error fetching referral discounts:', error);
        setDiscounts([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [customer_search, kind, status, start_date, end_date, page]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ customer_search: searchDraft.trim() });
  };

  return (
    <>
      <form onSubmit={applySearch} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label htmlFor="discountSearch" className={LABEL}>Customer Search</label>
            <input
              id="discountSearch"
              type="text"
              value={searchDraft}
              onChange={e => setSearchDraft(e.target.value)}
              placeholder="Name or code..."
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="discountKind" className={LABEL}>Type</label>
            <select id="discountKind" value={kind} onChange={e => setFilters({ kind: e.target.value })} className={INPUT}>
              <option value="">All</option>
              {Object.entries(REFERRAL_DISCOUNT_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="discountStatus" className={LABEL}>Status</label>
            <select id="discountStatus" value={status} onChange={e => setFilters({ status: e.target.value })} className={INPUT}>
              <option value="">All</option>
              {Object.entries(REFERRAL_DISCOUNT_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="discountStartDate" className={LABEL}>Earned From</label>
            <input
              id="discountStartDate"
              type="date"
              value={start_date}
              onChange={e => setFilters({ start_date: e.target.value })}
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="discountEndDate" className={LABEL}>Earned To</label>
            <input
              id="discountEndDate"
              type="date"
              value={end_date}
              onChange={e => setFilters({ end_date: e.target.value })}
              className={INPUT}
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600">Found {pluralise(totalCount, 'referral discount')}</p>
      </form>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <SummaryTile value={summary.earned} label={<>Discounts earned: {earnedBreakdown(summary)}</>} />
          <SummaryTile value={summary.available} label="Available" valueClassName="text-green-700" />
          <SummaryTile
            value={summary.reserved}
            label={`Reserved on open orders (${formatCurrency(summary.reserved_amount)})`}
            valueClassName="text-amber-700"
          />
          <SummaryTile value={summary.redeemed} label="Redeemed" valueClassName="text-blue-700" />
          <SummaryTile value={formatCurrency(summary.total_saved)} label="Total saved by customers" />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className={TH}>Earned</th>
                <th className={TH}>Customer</th>
                <th className={TH}>Type</th>
                <th className={TH}>Earned From</th>
                <th className={TH}>Status</th>
                <th className={TH}>Order</th>
                <th className={TH}>Saved</th>
                <th className={`hidden lg:table-cell ${TH}`}>Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">Loading referral discounts...</td></tr>
              ) : discounts.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">No referral discounts found</td></tr>
              ) : (
                discounts.map(discount => (
                  <tr key={discount.id} className="hover:bg-gray-50" data-testid={`referral-discount-${discount.id}`}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatReferralDate(discount.created_at)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <Link to={`/customers/${discount.customer_id}`} className="hover:underline">
                        <div className="text-sm font-medium text-gray-900">{discount.customer_name}</div>
                        <div className="text-sm text-gray-500">{discount.customer_code}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-4"><ReferralKindBadge kind={discount.kind} /></td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {discount.earned_from_customer_id ? (
                        <Link to={`/customers/${discount.earned_from_customer_id}`} className="hover:underline">{discount.earned_from}</Link>
                      ) : (
                        discount.earned_from
                      )}
                      {discount.earned_from_order_id && (
                        <div className="text-gray-500">
                          <Link to={`/orders/${discount.earned_from_order_id}`} className="text-blue-600 hover:underline">
                            {discount.earned_from_order_reference}
                          </Link>
                        </div>
                      )}
                      {discount.grant_reason && <div className="text-gray-500">{discount.grant_reason}</div>}
                    </td>
                    <td className="px-4 py-4"><ReferralDiscountStatusBadge status={discount.status} /></td>
                    <td className="px-4 py-4 text-sm">
                      {discount.order_id ? (
                        <Link to={`/orders/${discount.order_id}`} className="text-blue-600 hover:underline">{discount.order_reference}</Link>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      {discount.amount_saved !== null ? (
                        <span className="text-gray-900">{formatCurrency(Number(discount.amount_saved))}</span>
                      ) : discount.reserved_amount !== null ? (
                        <span className="text-gray-500" title="Taken off when the order is paid">{formatCurrency(Number(discount.reserved_amount))} (pending)</span>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-4 text-sm text-gray-500 max-w-xs whitespace-pre-line break-words">
                      {displayNotes(discount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </>
  );
};

export default ReferralDiscountsList;
